import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React__default, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from './Header.js';
import { TablePagination } from './Pagination/index.js';
import Rows from './Rows.js';
import { SINGLE_ROW_SELECT, MULTIPLE_ROW_SELECT } from './RowSelection/constants.js';
import SelectionCellAll from './RowSelection/SelectionCellAll.js';
import { sortData } from './SortUtility.js';

var _excluded = ["alternateRowColor", "cellPadding", "className", "data", "dataTestId", "fixedHeader", "height", "name", "onRowSelect", "pagination", "roundCorner", "rowSelection", "selectedRows", "showGrid", "scrollableWidth"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableAdvanced = function TableAdvanced(_ref) {
  var _data$defaultColDef4, _data$columnDefs;
  var alternateRowColor = _ref.alternateRowColor,
    _ref$cellPadding = _ref.cellPadding,
    cellPadding = _ref$cellPadding === void 0 ? 'normal' : _ref$cellPadding,
    className = _ref.className,
    data = _ref.data,
    dataTestId = _ref.dataTestId,
    fixedHeader = _ref.fixedHeader,
    height = _ref.height,
    name = _ref.name,
    onRowSelect = _ref.onRowSelect,
    pagination = _ref.pagination,
    roundCorner = _ref.roundCorner,
    rowSelection = _ref.rowSelection,
    selectedRows = _ref.selectedRows,
    _ref$showGrid = _ref.showGrid,
    showGrid = _ref$showGrid === void 0 ? true : _ref$showGrid,
    _ref$scrollableWidth = _ref.scrollableWidth,
    scrollableWidth = _ref$scrollableWidth === void 0 ? false : _ref$scrollableWidth,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  //manage currently sorting field name
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    activeSortColumn = _useState2[0],
    setActiveSortColumn = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    displayData = _useState4[0],
    setDisplayData = _useState4[1];

  //logic to be modified when we allow column re-ordering
  var columnOrder = data.columnDefs.map(function (_ref2) {
    var field = _ref2.field;
    return field;
  });

  //store the state of the header sort
  var _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    headerSort = _useState6[0],
    setHeaderSort = _useState6[1];

  //get the column that will act as each row's header for A11y,
  //error if one is not found or if multiples are found
  var columnRowHeader = data.columnDefs.filter(function (column) {
    return column.isRowHeader;
  });
  if (columnRowHeader.length < 1) {
    columnRowHeader = data.columnDefs[0].field; // set row header to first column in order to prevent erroring
    console.error("Hey Canvas User! Please add the prop 'isRowHeader' to one of your columns. This will set that column's data as the header for each row and will allow screen readers to read it out. Good candidates for row headers are data items that tell the user something about the entire row: email, store id, or product name may be good options.");
  } else if (columnRowHeader.length > 1) {
    columnRowHeader = data.columnDefs[0].field; // set row header to first column in order to prevent erroring
    console.error("Hey Canvas User! Multiple columns have the prop 'isRowHeader'. Please designate only one column as the row header column.");
  } else {
    columnRowHeader = columnRowHeader[0].field;
  }

  //check for duplicate field names
  var columnSet = new Set(columnOrder);

  //check for row selection
  var hasRowSelection = rowSelection === SINGLE_ROW_SELECT || rowSelection === MULTIPLE_ROW_SELECT;
  useEffect(function () {
    var sortDirection = headerSort.sortDirection,
      sortField = headerSort.sortField,
      sortType = headerSort.sortType;
    var copyArray = _toConsumableArray(data.rowData);
    if (sortDirection) {
      setDisplayData(sortData(copyArray, sortField, sortDirection, sortType));
    }
    if (!sortDirection) {
      setDisplayData(copyArray);
    }
  }, [headerSort, data.rowData]);

  //find invalid field names
  var hasStringWithSpace = columnOrder.map(function (field) {
    return field.indexOf(' ');
  }).some(function (spaceIndex) {
    return spaceIndex !== -1;
  });
  if (columnSet.size !== columnOrder.length || hasStringWithSpace) {
    console.error('Hey Canvas User, Fields in this data structure must be unique and have no spaces!');
  }
  var tableClasses = classnames('C-TableAdvanced', _defineProperty(_defineProperty({
    '--alternateRowColor': alternateRowColor
  }, "--cell-padding-".concat(cellPadding), cellPadding), '--showGrid', showGrid), className);
  var finalContainerClasses = classnames('C-TableAdvancedScrollContainer', {
    '--overflow-y': height,
    roundCorner: roundCorner,
    scrollableWidth: scrollableWidth
  }, {
    'C-TableAdvanced__selectionCell': hasRowSelection
  }, className);
  var headerClasses = classnames('C-TableAdvanced__header', {
    '--fixed': fixedHeader
  });
  var classesForColumns = data.columnDefs.reduce(function (obj, _ref3) {
    var field = _ref3.field,
      align = _ref3.align;
    return _objectSpread(_objectSpread({}, obj), {}, _defineProperty({}, field, classnames(_defineProperty({}, "--text-align-".concat(align), align))));
  }, {});
  var sortColumns = useMemo(function () {
    return data.columnDefs.reduce(function (obj, _ref4) {
      var _data$defaultColDef;
      var field = _ref4.field,
        sortType = _ref4.sortType,
        sortable = _ref4.sortable,
        defaultSort = _ref4.defaultSort,
        onSort = _ref4.onSort;
      var sortableColumn = sortable !== false ? sortable || (data === null || data === void 0 ? void 0 : (_data$defaultColDef = data.defaultColDef) === null || _data$defaultColDef === void 0 ? void 0 : _data$defaultColDef.sortable) : sortable;
      if (sortableColumn) {
        var _data$defaultColDef2, _data$defaultColDef3;
        return _objectSpread(_objectSpread({}, obj), {}, _defineProperty({}, field, {
          onSort: onSort ? onSort : data === null || data === void 0 ? void 0 : (_data$defaultColDef2 = data.defaultColDef) === null || _data$defaultColDef2 === void 0 ? void 0 : _data$defaultColDef2.onSort,
          sortDirection: defaultSort ? defaultSort : null,
          sortType: sortType ? sortType : data === null || data === void 0 ? void 0 : (_data$defaultColDef3 = data.defaultColDef) === null || _data$defaultColDef3 === void 0 ? void 0 : _data$defaultColDef3.sortType,
          sortable: sortableColumn
        }));
      }
      return obj;
    }, {}) || {};
  }, [data]);
  var onColumnSort = useCallback(function (sortField, sortDirection, sortType) {
    setHeaderSort({
      sortDirection: sortDirection,
      sortField: sortField,
      sortType: sortType
    });
  }, []);
  var generateHeaderCell = useCallback(function (columnData) {
    var fieldName = columnData.field;
    return /*#__PURE__*/React__default.createElement(Header, {
      key: fieldName,
      dataTestId: dataTestId,
      columnData: columnData,
      defaultColDef: data.defaultColDef,
      classesForColumn: classesForColumns[fieldName],
      activeSortColumn: activeSortColumn,
      setActiveSortColumn: setActiveSortColumn,
      onColumnSort: onColumnSort,
      columnSortDetails: sortColumns[fieldName]
    });
  }, [activeSortColumn, classesForColumns, data === null || data === void 0 ? void 0 : data.defaultColDef, onColumnSort, sortColumns, dataTestId]);
  var showTopPagination = (pagination === null || pagination === void 0 ? void 0 : pagination.location) === 'top' || (pagination === null || pagination === void 0 ? void 0 : pagination.location) === 'top-bottom';
  var showBottomPagination = (pagination === null || pagination === void 0 ? void 0 : pagination.location) !== 'top';
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, showTopPagination && /*#__PURE__*/React__default.createElement(TablePagination, {
    dataTestId: "".concat(dataTestId, "_top")
  }), /*#__PURE__*/React__default.createElement("div", {
    className: finalContainerClasses,
    tabIndex: "0",
    style: {
      maxHeight: "".concat(height, "px")
    }
  }, /*#__PURE__*/React__default.createElement("table", _extends({
    "aria-label": name,
    className: tableClasses,
    "data-testid": dataTestId,
    role: "table"
  }, otherProps), /*#__PURE__*/React__default.createElement("thead", {
    className: headerClasses
  }, /*#__PURE__*/React__default.createElement("tr", {
    className: "C-TableAdvanced__headerRow",
    role: "row",
    key: "row-header"
  }, /*#__PURE__*/React__default.createElement(SelectionCellAll, {
    dataTestId: dataTestId,
    displayData: displayData,
    isResizable: data === null || data === void 0 ? void 0 : (_data$defaultColDef4 = data.defaultColDef) === null || _data$defaultColDef4 === void 0 ? void 0 : _data$defaultColDef4.resizable,
    onRowSelect: onRowSelect,
    rowSelection: rowSelection,
    selectedRows: selectedRows,
    tableName: name
  }), data === null || data === void 0 ? void 0 : (_data$columnDefs = data.columnDefs) === null || _data$columnDefs === void 0 ? void 0 : _data$columnDefs.map(function (columnData) {
    return generateHeaderCell(columnData);
  }))), /*#__PURE__*/React__default.createElement("tbody", {
    className: "C-TableAdvanced__body"
  }, /*#__PURE__*/React__default.createElement(Rows, {
    classesForColumns: classesForColumns,
    columnOrder: columnOrder,
    columnRowHeader: columnRowHeader,
    dataTestId: dataTestId,
    displayData: displayData,
    onRowSelect: onRowSelect,
    rowSelection: rowSelection,
    selectedRows: selectedRows,
    showGrid: showGrid,
    tableName: name
  })), /*#__PURE__*/React__default.createElement("tfoot", {
    className: "C-TableAdvanced__footer"
  }))), showBottomPagination && /*#__PURE__*/React__default.createElement(TablePagination, {
    dataTestId: "".concat(dataTestId, "_bottom")
  }));
};

export { TableAdvanced, TableAdvanced as default };
